/**
 * <kr-slider> — the single carousel primitive used by every custom kr- section
 * (hero, announcement bar, testimonials, value props, product rows).
 *
 * Scroll-snap does the moving, so the slider still works with JS disabled or still
 * loading. This element only adds: active-dot tracking, prev/next, and autoplay.
 *
 * Attributes:
 *   autoplay="4000"  Autoplay interval in ms. Absent or "0" disables it.
 *   loop             Wrap from last slide back to first (and vice versa).
 *   infinite         Never rewind: the slides are cloned either side at runtime and the
 *                    track is silently repositioned once scrolling settles, so advancing
 *                    past the last slide carries straight on into the first. Implies
 *                    `loop`. Set by `kr-slider.liquid` from `infinite: true`.
 *   center           Track the slide on the track's centre line and mark it, so the
 *                    stylesheet can pull the others back. Set by `kr-slider.liquid`
 *                    from `center: true`.
 *   centered         Written by this element while a slide does hold the centre line.
 *
 * Refs (data attributes on descendants):
 *   [data-kr-track]  The scrolling viewport.
 *   [data-kr-slide]  Each slide. In `infinite` mode the clones carry it too, so "cells"
 *                    below means slides + clones, while `this.slides` stays the real ones
 *                    the dots and the change event count in.
 *   [data-kr-clone]  Added to the clones `infinite` mode builds.
 *   [data-kr-prev]   Previous button.
 *   [data-kr-next]   Next button.
 *   [data-kr-dot]    A dot button; its index comes from its position in the list.
 */
class KrSlider extends HTMLElement {
  #observer = null;
  #centerObserver = null;
  #resizeObserver = null;
  #onCenterLine = new Set();
  #visible = new Set();
  #cells = [];
  #cellIndex = 0;
  #settleTimer = null;
  #timer = null;
  #paused = false;
  #index = 0;
  #interval = 0;
  #onVisibilityChange = null;
  #dragAbort = null;
  #dragging = false;

  connectedCallback() {
    this.track = this.querySelector('[data-kr-track]');
    if (!this.track) return;

    this.slides = Array.from(this.querySelectorAll('[data-kr-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-kr-dot]'));
    this.center = this.hasAttribute('center');
    if (this.slides.length < 2) {
      this.#toggleControls(false);
      return;
    }

    this.infinite = this.hasAttribute('infinite');
    this.loop = this.infinite || this.hasAttribute('loop');
    this.#cells = this.infinite ? this.#buildLoop() : [...this.slides];

    this.track.addEventListener('pointerdown', this.#onPointerDown);

    this.querySelector('[data-kr-prev]')?.addEventListener('click', () => this.#step(-1));
    this.querySelector('[data-kr-next]')?.addEventListener('click', () => this.#step(1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));

    // IntersectionObserver rather than a scroll listener: no per-frame work, and it
    // reports the truly visible slide even when several are in view at once.
    this.#observer = new IntersectionObserver(
      (entries) => {
        // Entries only cover the slides whose visibility *changed*, so membership is kept
        // and the left-most is picked from everything on screen. Reading it off the batch
        // instead would name whichever slide had just appeared on the right.
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.#visible.add(entry.target);
          else this.#visible.delete(entry.target);
        });

        let first = null;
        this.#visible.forEach((cell) => {
          if (!first || cell.offsetLeft < first.offsetLeft) first = cell;
        });
        if (!first) return;

        this.#cellIndex = this.#cells.indexOf(first);
        this.#setIndex(this.#realIndexOf(first));
      },
      { root: this.track, threshold: 0.6 }
    );
    this.#cells.forEach((cell) => this.#observer.observe(cell));

    if (this.center) this.#watchCenter();
    if (this.infinite || !this.loop) this.#watchSettle();

    // Controls are pointless when every slide already fits — a row of 5 shown 5-up has
    // nothing to scroll to. Observed rather than computed once: whether the track
    // overflows changes with the viewport. Fires on observe, so it sets the initial state.
    this.#resizeObserver = new ResizeObserver(() => {
      this.#toggleControls(this.track.scrollWidth > this.track.clientWidth + 1);
    });
    this.#resizeObserver.observe(this.track);

    this.#updateArrows();
    this.#setupAutoplay();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#centerObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#onCenterLine.clear();
    this.#visible.clear();
    clearTimeout(this.#settleTimer);
    // A theme-editor re-render reconnects the same element, and #buildLoop would clone the
    // clones on top of the ones already here.
    this.#cells.forEach((cell) => cell.hasAttribute('data-kr-clone') && cell.remove());
    this.#cells = [];
    this.#stop();
    this.#dragAbort?.abort();
    this.track?.style.removeProperty('scroll-snap-type');
    if (this.#onVisibilityChange) {
      document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    }
  }

  get index() {
    return this.#index;
  }

  goTo(index) {
    // In infinite mode the same slide exists three times over; scroll to whichever copy is
    // closest, so a dot click nudges sideways instead of rewinding the whole track.
    const target = this.infinite ? this.#nearestCopyOf(index) : this.slides[index];
    if (!target) return;
    this.#cellIndex = this.#cells.indexOf(target);
    this.#scrollTo(target);
  }

  #scrollTo(cell) {
    this.track.scrollTo({
      left: this.track.scrollLeft + this.#scrollDeltaFor(cell),
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  // Lays a copy of the slides either side of the real ones and parks the track on the
  // middle copy, so there is always a slide waiting in both directions. The clones are
  // built here rather than in Liquid to keep the duplicated reviews out of the markup —
  // out of the page source, and out of the accessibility tree via aria-hidden/inert.
  #buildLoop() {
    const copy = () =>
      this.slides.map((slide) => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('data-kr-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        // Section-editor ids must not appear twice, or clicking a review in the theme
        // editor could scroll to, or highlight, a copy of it.
        for (const { name } of [...clone.attributes]) {
          if (name.startsWith('data-shopify')) clone.removeAttribute(name);
        }
        clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        return clone;
      });

    const before = copy();
    const after = copy();
    const track = this.track;
    // Inserting and repositioning in one synchronous block: the browser never gets a
    // chance to paint the track sitting on the leading clones.
    track.prepend(...before);
    track.append(...after);
    const cells = [...before, ...this.slides, ...after];
    track.scrollLeft = this.#copyWidth(cells);
    this.#cellIndex = this.slides.length;
    return cells;
  }

  // Distance from a cell to its twin one copy along — how far to shift to swap copies.
  #copyWidth(cells = this.#cells) {
    return cells[this.slides.length].offsetLeft - cells[0].offsetLeft;
  }

  #realIndexOf(cell) {
    const at = this.#cells.indexOf(cell);
    return at < 0 ? -1 : at % this.slides.length;
  }

  #nearestCopyOf(index) {
    let nearest = null;
    let best = Infinity;
    for (let at = index; at < this.#cells.length; at += this.slides.length) {
      const distance = Math.abs(this.#scrollDeltaFor(this.#cells[at]));
      if (distance < best) {
        best = distance;
        nearest = this.#cells[at];
      }
    }
    return nearest;
  }

  // Work that has to wait for scrolling to stop: hopping back to the middle copy (the slide
  // under the viewport is identical either side of the hop, so there is nothing to see — it
  // just restores the runway in both directions), and the end-of-track arrow states, which
  // no slide crossing the visibility threshold would otherwise announce.
  #watchSettle() {
    const settled = () => {
      if (this.infinite) this.#recenterCopy();
      this.#updateArrows();
    };
    if ('onscrollend' in window) {
      this.track.addEventListener('scrollend', settled);
      return;
    }
    // Safari < 18 has no scrollend, so fall back to a quiet-scroll timer.
    this.track.addEventListener(
      'scroll',
      () => {
        clearTimeout(this.#settleTimer);
        this.#settleTimer = setTimeout(settled, 150);
      },
      { passive: true }
    );
  }

  #recenterCopy() {
    // Mid-drag the handler owns scrollLeft, and its own scrollend pass will settle this.
    if (this.#dragging) return;

    const nearest = this.#nearestCell();
    if (nearest < 0) return;

    const copies = Math.floor(nearest / this.slides.length) - 1;
    if (copies === 0) return;

    this.track.scrollLeft -= copies * this.#copyWidth();
    this.#cellIndex = nearest - copies * this.slides.length;
  }

  #nearestCell() {
    let nearest = -1;
    let best = Infinity;
    this.#cells.forEach((cell, at) => {
      const distance = Math.abs(this.#scrollDeltaFor(cell));
      if (distance < best) {
        best = distance;
        nearest = at;
      }
    });
    return nearest;
  }

  // How far the track still has to scroll to bring `slide` into place. Which place that
  // is comes from the slide's own `scroll-snap-align`, so this follows the stylesheet
  // across breakpoints instead of duplicating them — centre mode is centre-snapped on
  // mobile but start-snapped once whole slides fit.
  #scrollDeltaFor(slide) {
    if (!this.center || getComputedStyle(slide).scrollSnapAlign.split(' ').pop() !== 'center') {
      return slide.offsetLeft - this.track.offsetLeft - this.track.scrollLeft;
    }
    return this.#centerOffsetOf(slide);
  }

  // How far `slide` sits from the middle of the track. Rects, so it holds whatever the
  // track's padding is, and ignores the off-centre slides' scale: they shrink about their
  // own centre, which is the point being measured, so a scaled slide reports the same
  // centre it has at scale 1.
  #centerOffsetOf(slide) {
    const slideBox = slide.getBoundingClientRect();
    const trackBox = this.track.getBoundingClientRect();
    return slideBox.left + slideBox.width / 2 - (trackBox.left + trackBox.width / 2);
  }

  // Marks the slide holding the middle of the track so the stylesheet can pull the others
  // back. Separate from #index on purpose: with 3 slides up, the centred card is the
  // middle of the three while the current slide — the one the dots and prev/next count
  // in — is still the left-most, exactly as it is for every other kr- carousel.
  #watchCenter() {
    this.#centerObserver = new IntersectionObserver(
      (entries) => {
        // Entries only cover the slides whose state *changed*, so membership is kept
        // rather than derived from the batch: a batch holding just the slide that left
        // would otherwise read as "nothing is centred" and flicker the whole row.
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.#onCenterLine.add(entry.target);
          else this.#onCenterLine.delete(entry.target);
        });
        this.#markCentered();
      },
      // Shrinks the root to a hairline down the middle of the track, so membership means
      // "covers the centre" exactly — and changes at exactly the scroll offset where the
      // emphasis should move on. Asking a wider band for its nearest slide instead would
      // miss those handovers: they happen with both slides in the band and nothing
      // entering or leaving it, so no callback would run.
      { root: this.track, rootMargin: '0px -49.9% 0px -49.9%', threshold: 0 }
    );
    this.#cells.forEach((cell) => this.#centerObserver.observe(cell));
  }

  // Nothing on the hairline — an even slides-per-view, where the middle of the track falls
  // in the gap between two slides — means there is no centred card to single out, and the
  // row is left uniform.
  #markCentered() {
    let middle = null;
    let best = Infinity;
    // Rounding can put two slides on the hairline at once; the nearer one is the centre.
    this.#onCenterLine.forEach((slide) => {
      const distance = Math.abs(this.#centerOffsetOf(slide));
      if (distance < best) {
        best = distance;
        middle = slide;
      }
    });

    this.toggleAttribute('centered', Boolean(middle));
    this.#cells.forEach((cell) => {
      cell.classList.toggle('kr-slider__slide--centered', cell === middle);
    });
  }

  // Scroll-snap gives touch devices native swiping, but a mouse has no equivalent,
  // so desktop drags are translated to scrollLeft by hand. Touch pointers are left
  // alone entirely — intercepting them would fight the native scroll.
  #onPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0 || this.#dragging) return;

    event.preventDefault();

    const startX = event.clientX;
    const startScroll = this.track.scrollLeft;
    let moved = false;

    const controller = new AbortController();
    const { signal } = controller;
    this.#dragAbort = controller;
    this.#dragging = true;
    this.#paused = true;

    // Mandatory snap would yank the track back to a snap point on every scrollLeft
    // write, so it stays off for the whole session and only returns once the
    // release animation has settled on a slide.
    this.track.style.scrollSnapType = 'none';

    // A real drag ends with a click on whatever is under the cursor; swallow it
    // so links inside slides don't navigate. Deliberately NOT tied to `signal`:
    // the click fires after pointerup, when the controller is already aborted.
    const suppressClick = (clickEvent) => {
      clickEvent.preventDefault();
      clickEvent.stopPropagation();
    };

    const onPointerMove = (e) => {
      const delta = startX - e.clientX;
      if (!moved) {
        if (Math.abs(delta) < 3) return;
        moved = true;
        this.track.setPointerCapture(e.pointerId);
        this.setAttribute('dragging', '');
        document.addEventListener('click', suppressClick, { capture: true, once: true });
      }
      this.track.scrollLeft = startScroll + delta;
    };

    const onPointerUp = () => {
      controller.abort();
      this.#dragAbort = null;
      this.#dragging = false;
      this.removeAttribute('dragging');
      if (moved) {
        // The drag's own click (if any) dispatches synchronously before this
        // timeout, so this only cleans up sessions that ended without a click
        // (pointercancel, release outside the window) — otherwise the one-shot
        // listener would swallow the user's next unrelated click.
        setTimeout(() => document.removeEventListener('click', suppressClick, true), 0);
      }

      if (!moved) {
        this.track.style.scrollSnapType = '';
        return;
      }

      // The observer's #index lags, so pick the nearest cell from the live scroll
      // position instead: the one needing the smallest correction from here.
      const nearest = this.#nearestCell();
      if (nearest >= 0) {
        this.#cellIndex = nearest;
        this.#scrollTo(this.#cells[nearest]);
      }

      const restore = () => {
        if (this.#dragging) return;
        this.track.style.scrollSnapType = '';
      };
      this.track.addEventListener('scrollend', restore, { once: true });
      setTimeout(restore, 700); // scrollend is missing in Safari < 18

      // A release outside the slider doesn't always deliver the pointerleave
      // that would resume autoplay, so settle #paused from the real hover state.
      if (!this.matches(':hover')) this.#paused = false;
    };

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
  };

  #step(direction) {
    // Infinite mode steps through the cells, so it walks off the last slide into the
    // leading copy of the first rather than scrolling all the way back.
    if (this.infinite) {
      const next = this.#cells[this.#cellIndex + direction];
      if (!next) return;
      this.#cellIndex += direction;
      this.#scrollTo(next);
      return;
    }

    let next = this.#index + direction;
    if (next < 0) next = this.loop ? this.slides.length - 1 : 0;
    if (next > this.slides.length - 1) next = this.loop ? 0 : this.slides.length - 1;
    this.goTo(next);
  }

  #setIndex(index) {
    if (index < 0 || index === this.#index) return;
    this.#index = index;

    this.dots.forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle('kr-slider__dot--active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    this.#updateArrows();

    // Lets outside UI (the product gallery's thumbnail rail) follow a swipe.
    this.dispatchEvent(new CustomEvent('kr-slider:change', { detail: { index }, bubbles: true }));
  }

  // Driven by how far the track can still scroll rather than by the current index: with
  // several slides in view the left-most one never reaches the last index, so a row of 5
  // shown 4 up would keep `next` enabled at the end of the track.
  #updateArrows() {
    if (this.loop) return;

    const prev = this.querySelector('[data-kr-prev]');
    const next = this.querySelector('[data-kr-next]');
    const end = this.track.scrollWidth - this.track.clientWidth;
    if (prev) prev.disabled = this.track.scrollLeft <= 1;
    if (next) next.disabled = this.track.scrollLeft >= end - 1;
  }

  #toggleControls(show) {
    this.querySelectorAll('[data-kr-prev], [data-kr-next], .kr-slider__dots').forEach((el) => {
      el.hidden = !show;
    });
  }

  #setupAutoplay() {
    const interval = Number(this.getAttribute('autoplay')) || 0;
    if (!interval || matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#interval = interval;

    const pause = () => {
      this.#paused = true;
    };
    const resume = () => {
      this.#paused = false;
    };

    this.addEventListener('pointerenter', pause);
    this.addEventListener('pointerleave', resume);
    this.addEventListener('focusin', pause);
    this.addEventListener('focusout', resume);

    // Held on the instance so disconnectedCallback can detach it. Section re-renders in
    // the theme editor would otherwise leave one listener behind per edit.
    this.#onVisibilityChange = () => {
      if (document.hidden) this.#stop();
      else this.#start();
    };
    document.addEventListener('visibilitychange', this.#onVisibilityChange);

    this.#start();
  }

  #start() {
    if (this.#timer || !this.#interval) return;
    this.#timer = setInterval(() => {
      if (this.#paused) return;

      // Infinite mode has a slide waiting in both directions at all times, so there is no
      // end to handle: just keep stepping.
      if (this.infinite) return this.#step(1);

      const last = this.slides.length - 1;
      if (this.#index >= last) {
        // Matches #step(): a non-looping slider stops at the end rather than snapping back.
        if (!this.loop) return this.#stop();
        return this.goTo(0);
      }

      this.goTo(this.#index + 1);
    }, this.#interval);
  }

  #stop() {
    if (!this.#timer) return;
    clearInterval(this.#timer);
    this.#timer = null;
  }
}

if (!customElements.get('kr-slider')) {
  customElements.define('kr-slider', KrSlider);
}
